import React from "react"
import styled from "styled-components"
import IconArrow from "../atoms/iconArrow"
import IconAtsi from "../atoms/iconAtsi"
import Pin from "../../assets/pin.inline.svg"
import PropTypes from "prop-types"
import LogoSvg from "../../assets/footer-banner.inline.svg"

const Holder = styled.footer`
  z-index: 10;
  margin-top: -1px;
  color: ${(props) => props.theme.colours[props.color]};
  background-color: ${(props) => props.theme.colours.black};
  p {
    ${(props) => props.theme.fluidType(-2)};
  }

  @media (${(props) => props.theme.breakpoints.md}) {
    min-height: 95vh;
  }

  .inner-wrapper {
    @media (${(props) => props.theme.breakpoints.md}) {
      min-height: 95vh;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 100%;
    }
  }
`

const Contents = styled.div`
  max-width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  padding: 1rem 0 1rem 0;

  @media (${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 3fr 2fr 1fr;
  }
`

const Acknowledgement = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 1rem;
  @media (${(props) => props.theme.breakpoints.md}) {
    grid-column: 1/2;
    flex-direction: column;
    padding-left: 1rem;
  }
  svg {
    width: 2.5rem;
    height: auto;
    display: block;
  }
`

const Links = styled.div`
  margin-top: 1rem;
  padding: 0 1rem;

  @media (${(props) => props.theme.breakpoints.md}) {
    margin-top: 0;
    grid-column: 3/4;
  }
  p {
    margin: 0;
  }
  p .podcast-link {
    margin-bottom: 0.5rem;
    @media (${(props) => props.theme.breakpoints.md}) {
      margin-bottom: 1.5rem;
    }
  }
  .arrow-link {
    margin-bottom: 0.5rem;
    @media (${(props) => props.theme.breakpoints.md}) {
      margin-bottom: 0;
    }
    svg {
      margin-right: 1rem;
      height: 0.8rem;
      width: auto;
      path {
        stroke: ${(props) => props.theme.colours[props.color]};
      }
    }
  }

  @keyframes bounce1 {
    0% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 1;
    }
  }

  @keyframes bounce2 {
    25% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 1;
    }
  }

  @keyframes bounce3 {
    50% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 1;
    }
  }

  #circle-1 {
    animation: bounce1 2s ease-out infinite;
  }

  #circle-2 {
    animation: bounce2 2s ease-out infinite;
  }

  #circle-3 {
    animation: bounce3 2s ease-out infinite;
  }
`

const Locations = styled.div`
  position: relative;
  max-width: 100%;
  margin: 0 auto;
  min-height: 10rem;
  height: 100%;
  width: 100%;

  .location {
    ${(props) => props.theme.fluidType(0)};
  }

  .location-australia {
    position: absolute;
    top: 0;
    left: 30vw;
  }

  .location-singapore {
    position: absolute;
    top: 50%;
    left: 65vw;
  }

  .location-uk {
    position: absolute;
    bottom: 0;
    left: 45vw;
  }
  svg {
    width: auto;
    height: 0.8rem;
    display: inline;
    margin-right: 0.5rem;
  }
`
const BannerHolder = styled.div`
  position: relative;
  width: calc(100%-2rem);
  margin: 0 1rem;
  svg {
    width: 100%;
    height: auto;
    display: block;
  }
`

const Copyright = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  p {
    margin: 0;
    font-size: 1.3vw;
  }
`

const Attribution = styled.div`
  margin: 1rem;
  text-align: left;
  font-size: ${(props) => props.theme.fluidType(-4)};
  font-size: 12px;

  @media (${(props) => props.theme.breakpoints.md}) {
    text-align: right;
  }
`

function Footer({ color }) {
  return (
    <Holder color={color}>
      <div className="inner-wrapper">
        <Contents>
          <Acknowledgement>
            <IconAtsi />
            <p>
              In the spirit of reconciliation CraigWalker acknowledges the
              Traditional Custodians of country throughout Australia and their
              connections to land, sea and community. We pay our respect to
              their elders past and present and extend that respect to all
              Aboriginal and Torres Strait Islander peoples&nbsp;today.
            </p>
          </Acknowledgement>

          <Links color={color}>
            <p>
              <a
                className="arrow-link podcast-link"
                href="https://open.spotify.com/show/68cgUF0e2eJQcNsrpQF0jb?si=StleZEZ8QiiEsyYsaG5qrQ"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Pin />
                <span>CW Podcast</span>
              </a>
            </p>

            <p>
              <a
                className="arrow-link"
                href="https://www.linkedin.com/company/craig-walker/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconArrow />
                <span>Linkedin</span>
              </a>
              <a
                className="arrow-link"
                href="https://www.instagram.com/craigwalkerstudio/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconArrow />
                <span>Instagram</span>
              </a>
              <a
                className="arrow-link"
                href="https://medium.com/@craig_walker"
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconArrow />
                <span>Medium</span>
              </a>
            </p>
          </Links>
        </Contents>
        <Locations>
          <div className="location-australia">
            <p className="location">
              <Pin />
              <span>Australia</span>
            </p>
          </div>
          <div className="location-singapore">
            <p className="location">
              <Pin />
              <span>Singapore</span>
            </p>
          </div>
          <div className="location-uk">
            <p className="location">
              <Pin />
              <span>UK</span>
            </p>
          </div>
        </Locations>
        <Attribution>
          Built by{" "}
          <a href="https://supermarket.london/" target="_blank">
            Supermarket
          </a>
        </Attribution>
        <BannerHolder>
          <LogoSvg />

          <Copyright>
            <p>&copy; Craig Walker {new Date().getFullYear()}</p>
          </Copyright>
        </BannerHolder>
      </div>
    </Holder>
  )
}

Footer.propTypes = {
  color: PropTypes.string,
}

Footer.defaultProps = {
  color: "white",
}

export default Footer
